import axios from 'axios';
import authenticatedApiClient, { installRequestContextInterceptors } from './authenticatedApiClient';

// Shared axios instance for backend API calls. All service functions below use the
// same base URL and API key header so request behavior stays consistent.
const api = axios.create({
    baseURL: process.env.VUE_APP_BACKEND_API_URL || 'http://localhost:3000/api',
    headers: {
        'X-API-KEY': process.env.VUE_APP_API_KEY,
    }
});

installRequestContextInterceptors(api);

const PUBLIC_SNAPSHOT_VERSION = 1;

async function readPublicSnapshot(response, label) {
    if (!response.ok) {
        throw new Error(`${label} not found (${response.status})`);
    }
    const payload = await response.json();
    if (
        payload?.meta?.snapshotVersion !== PUBLIC_SNAPSHOT_VERSION
        || payload?.meta?.privacyScope !== 'public'
    ) {
        throw new Error(`${label} is legacy, malformed, or not public`);
    }
    return payload;
}

export const fetchTodayEvents = async (date) => {
    try {
        const response = await authenticatedApiClient.get(`/calendar/by-date?date=${date}`);
        return response.data.events; // Ensure it returns the `events` array
    } catch (error) {
        console.error('Error fetching events:', error);
        throw error; // Propagate the error to the caller
    }
};

export const fetchPlaces = async (opts = {}) => {
    try {
        // Build query params explicitly so optional filters are omitted when empty.
        const params = {};
        if (opts.types?.length) params.types = opts.types.join(',');
        if (opts.year) params.year = String(opts.year);
        if (opts.from) params.from = opts.from;
        if (opts.to) params.to = opts.to;

        // NIEUW: personIDs
        if (opts.personIDs?.length) params.personIDs = opts.personIDs.join(',');

        const response = await api.get('/places', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching places:', error);
        throw error;
    }
};

const buildPlaceSurnameParams = (opts = {}) => {
    const params = {
        zoom: String(opts.zoom ?? 2),
    };
    if (opts.types?.length) params.types = opts.types.join(',');
    if (opts.year) params.year = String(opts.year);
    if (opts.from) params.from = opts.from;
    if (opts.to) params.to = opts.to;
    if (opts.personIDs?.length) params.personIDs = opts.personIDs.join(',');
    return params;
};

export const fetchPlaceSurnames = async (opts = {}) => {
    try {
        const response = await authenticatedApiClient.get('/place-surnames', {
            params: buildPlaceSurnameParams(opts),
            ...(opts.signal ? { signal: opts.signal } : {}),
        });
        return response.data;
    } catch (error) {
        if (error?.code !== 'ERR_CANCELED') {
            console.error('Error fetching place surnames:', error);
        }
        throw error;
    }
};

export const fetchPlaceSurnameProfile = async (surname, opts = {}) => {
    try {
        const response = await authenticatedApiClient.get('/place-surnames/profile', {
            params: {
                ...buildPlaceSurnameParams(opts),
                surname,
            },
            ...(opts.signal ? { signal: opts.signal } : {}),
        });
        return response.data;
    } catch (error) {
        if (error?.code !== 'ERR_CANCELED') {
            console.error('Error fetching place surname profile:', error);
        }
        throw error;
    }
};

export const fetchPlaceSummary = async (opts = {}) => {
    try {
        const params = {};
        if (opts.types?.length) params.types = opts.types.join(',');
        if (opts.year) params.year = String(opts.year);
        if (opts.from) params.from = opts.from;
        if (opts.to) params.to = opts.to;
        const { data } = await api.get('/places/summary', { params });
        return data; // { totalPlaces, typeCounts }
    } catch (error) {
        console.error('Error fetching place summary:', error);
        throw error;
    }
};

export const fetchPlace = async (placeId) => {
    try {
        const response = await api.get(`/places/${placeId}`); // Make a GET request to /places/:id
        return response.data; // Return the place object
    } catch (error) {
        console.error('Error fetching place:', error);
        throw error; // Propagate the error to the caller
    }
};

export const fetchPlaceEvents = async (placeId) => {
    try {
        const response = await authenticatedApiClient.get(`/places/${placeId}/events`);
        return response.data; // Returns the events array
    } catch (error) {
        console.error('Error fetching place events:', error);
        throw error;
    }
};

// New function to fetch nested places
export const fetchNestedPlaces = async () => {
    try {
        const response = await api.get('/places/nested'); // Make a GET request to /places/nested
        return response.data; // Return the nested hierarchy of places
    } catch (error) {
        console.error('Error fetching nested places:', error);
        throw error; // Propagate the error to the caller
    }
};

export const fetchDocuments = async (page = 1, resultsPerPage = 20, search = '', category = '') => {
    const response = await api.get('/documents', {
        params: { page, resultsPerPage, search, category }, // Add filter parameters as query params
    });
    return response.data;
};

export const fetchPhotos = async (page = 1, resultsPerPage = 20, search = '', albumID = null) => {
    const response = await api.get('/photos', {
        params: { page, resultsPerPage, search, albumID },
    });
    return response.data;
};

export const fetchMedia = async (
    page = 1,
    resultsPerPage = 20,
    search = '',
    type = 'all',
    albumID = null,
    category = ''
) => {
    const response = await api.get('/media', {
        params: { page, resultsPerPage, search, type, albumID, category },
    });
    return response.data;
};

export const fetchAlbums = async () => {
    const response = await api.get('/albums'); // Fetch all albums
    return response.data.albums; // Return only the albums array
};

export const fetchCurrentSiteStats = async () => {
    try {
        // Run independent dashboard requests in parallel to reduce total latency.
        const [people, documents, photos, places, lastnames] = await Promise.all([
            api.get('/total-people'),
            api.get('/total-documents'),
            api.get('/total-photos'),
            api.get('/total-places'),
            api.get('/unique-lastnames')
        ]);

        return {
            totalPeople: people.data.total,
            totalDocuments: documents.data.totalDocuments,
            totalPhotos: photos.data.totalPhotos,
            totalPlaces: places.data.totalPlaces,
            uniqueLastnames: lastnames.data.uniqueLastnames
        };
    } catch (error) {
        console.error('Error fetching current site stats:', error);
        throw error;
    }
};

export const fetchPeopleDashboardStats = async () => {
    try {
        const response = await api.get('/people-dashboard-stats');
        return response.data;
    } catch (error) {
        console.error('Error fetching people dashboard stats:', error);
        throw error;
    }
};

export const fetchBloodRelativesStats = async (opts = {}) => {
    try {
        // Backend expects string flags for optional toggles.
        const response = await authenticatedApiClient.get('/statistieken/bloedverwanten', {
            params: {
                ...(opts.includeGroups ? { includeGroups: '1' } : {}),
                ...(opts.refresh ? { refresh: '1' } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching blood relatives stats:', error);
        throw error;
    }
};

export const fetchBloodRelativesStaticFile = async () => {
    // Static JSON is preferred for speed; callers can fall back to API if missing.
    const response = await fetch('/data/bloedverwanten.json', {
        cache: 'no-store',
        headers: {
            Accept: 'application/json',
        },
    });
    return readPublicSnapshot(response, 'Static blood relatives file');
};

export const fetchOverzichtStaticFile = async () => {
    // Keep this uncached so dashboard refreshes are immediately visible.
    const response = await fetch('/data/overzicht-stats.json', {
        cache: 'no-store',
        headers: {
            Accept: 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error(`Static overzicht stats file not found (${response.status})`);
    }
    return response.json();
};

export const fetchAllPeopleGraph = async (opts = {}) => {
    try {
        const response = await authenticatedApiClient.get('/people-graph', {
            params: {
                ...(opts.includeSiblings ? { includeSiblings: '1' } : {}),
                ...(opts.maxNodes ? { maxNodes: String(opts.maxNodes) } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching all-people graph:', error);
        throw error;
    }
};

export const fetchAllPeopleGraphStaticFile = async () => {
    const response = await fetch('/data/all-people-graph.json', {
        cache: 'no-store',
        headers: {
            Accept: 'application/json',
        },
    });
    return readPublicSnapshot(response, 'Static all-people graph file');
};

export const fetchBloodRelativeIndividuals = async (path, half = null) => {
    try {
        const response = await authenticatedApiClient.get('/statistieken/bloedverwanten/individuen', {
            params: {
                path,
                ...(half === null ? {} : { half: half ? '1' : '0' }),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching blood relative individuals:', error);
        throw error;
    }
};

export const fetchPersonDefaultPhoto = async (personID) => {
    try {
        const response = await api.get(`/person-default-photo/${personID}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching person default photo:', error);
        throw error;
    }
};

export const fetchServerStats = async () => {
    try {
        const response = await authenticatedApiClient.get('/server-stats');
        return response.data;
    } catch (error) {
        console.error('Error fetching server stats:', error);
        throw error;
    }
};

export const fetchDnaSegmentEntries = async () => {
    try {
        const response = await authenticatedApiClient.get('/dna-segments/entries');
        return response.data;
    } catch (error) {
        console.error('Error fetching DNA segment entries:', error);
        throw error;
    }
};

export const fetchDnaTesters = async () => {
    try {
        const response = await authenticatedApiClient.get('/dna-testers');
        return response.data;
    } catch (error) {
        console.error('Error fetching DNA tester configuration:', error);
        throw error;
    }
};

export const fetchDnaMatches = async () => {
    try {
        const response = await authenticatedApiClient.get('/dna-matches');
        return response.data;
    } catch (error) {
        console.error('Error fetching DNA matches:', error);
        throw error;
    }
};

export const fetchDnaSegmentsByEntry = async (entry) => {
    try {
        // Entry identity is a (testerCategory, personID) pair.
        const response = await authenticatedApiClient.get('/dna-segments/segments', {
            params: {
                ...(entry?.entryKey ? { entryKey: entry.entryKey } : {
                    testerCategory: entry?.testerCategory,
                    personID: entry?.personID,
                }),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching DNA segments by entry:', error);
        throw error;
    }
};

export const fetchDnaSegmentAncestryOverlay = async (entry) => {
    try {
        // Overlay data is optional and may be unavailable for some entries.
        const response = await authenticatedApiClient.get('/dna-segments/ancestry-overlay', {
            params: {
                ...(entry?.entryKey ? { entryKey: entry.entryKey } : {
                    testerCategory: entry?.testerCategory,
                    personID: entry?.personID,
                }),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching DNA ancestry overlay by entry:', error);
        throw error;
    }
};

export const fetchDnaAncestorPainting = async () => {
    try {
        const response = await authenticatedApiClient.get('/dna-segments/ancestor-painting');
        return response.data;
    } catch (error) {
        console.error('Error fetching DNA ancestor painting:', error);
        throw error;
    }
};

export const fetchDnaReconstructedAncestors = async () => {
    try {
        const response = await authenticatedApiClient.get('/dna-segments/reconstructed-ancestors');
        return response.data;
    } catch (error) {
        console.error('Error fetching reconstructed DNA ancestors:', error);
        throw error;
    }
};

export default api;
